import { ref } from 'vue';
import { UserServerResponse } from 'src/core/types/user';

const app_id = import.meta.env.VITE_INTERCOM_APP_ID;

/**
 * Reactive flag reflecting whether the Intercom messenger panel is currently
 * open. Kept in sync via the SDK's onShow/onHide hooks so any UI (e.g. the
 * sidebar "Get help" item) can reactively mirror the open state.
 */
export const isIntercomOpen = ref(false);

/**
 * Reactive flag reflecting whether Intercom was successfully initialized and
 * is available to receive commands. Flipped after `initializeIntercom` boots
 * the SDK, reset on shutdown. UI that is only meaningful when Intercom is
 * reachable (e.g. the sidebar "Get help" item) can hide itself when this is
 * false — e.g. when `VITE_INTERCOM_APP_ID` is unset.
 */
export const isIntercomAvailable = ref(false);

export const initializeIntercom = async (user: UserServerResponse) => {
  if (!user) return;
  if (!app_id) return;

  const {
    default: Intercom,
    onShow,
    onHide,
  } = await import('@intercom/messenger-js-sdk');

  const { id, first_name, last_name, email, customer } = user;
  const user_id = id.toString();
  const name = `${first_name} ${last_name}`;
  const created_at = customer?.created_at
    ? Math.floor(new Date(customer.created_at).getTime() / 1000)
    : Math.floor(Date.now() / 1000);

  Intercom({
    app_id,
    user_id,
    name,
    email,
    created_at,
    hide_default_launcher: true,
  });

  // Route the Messenger through the per-workspace alternate domain instead of the
  // default api-iam.intercom.io. Some Vietnamese ISPs refuse connections to
  // Intercom's default endpoint (ERR_CONNECTION_REFUSED); this is Intercom's
  // recommended workaround. The messenger-js-sdk ignores an api_base passed into
  // Intercom() — it overwrites it from `region` — so we set it directly on
  // intercomSettings here, before the async widget script reads it on load.
  if (window.intercomSettings) {
    window.intercomSettings.api_base = `https://${app_id}.intercom-messenger.com`;
  }

  isIntercomAvailable.value = true;

  onShow(() => {
    isIntercomOpen.value = true;
  });
  onHide(() => {
    isIntercomOpen.value = false;
  });
};

export const shutdownIntercom = async () => {
  if (typeof window.Intercom === 'function') {
    window.Intercom('shutdown');
  }
  isIntercomOpen.value = false;
  isIntercomAvailable.value = false;
};

export const showIntercom = () => {
  if (typeof window.Intercom === 'function') {
    window.Intercom('show');
  }
};

export const isIntercomActive = () => {
  return typeof window.Intercom === 'function';
};
